import {
  ActionError,
  Button,
  Field,
  FieldGrid,
  FormBlock,
  FormFooter,
  SavedIndicator,
  Skeleton,
  Textarea,
  toast,
} from '@westhetics/ui'
import { useRef, useState, type FormEvent } from 'react'
import { LoadError } from '@/app/load-error'
import { TextField } from '@/features/clinic-profile/blocks'
import { placeFieldErrors } from '@/features/clinic-profile/form'
import { focusFirstInvalid, useFormState } from '@/features/clinic-profile/use-form-state'
import { errorMessage } from '@/lib/errors'
import { useIntegrationsCatalog, useUpdateIntegration, type Integration } from './api'
import {
  INTEGRATION_KEYS,
  integrationFieldKey,
  integrationInput,
  validateIntegration,
  type IntegrationFormState,
} from './form'
import { PermissionsFields } from './permissions-fields'

/**
 * Aba Permissões: o nome, a descrição e os escopos do app (RN-ADM-43). Vale na próxima chamada do app, porque os
 * escopos são lidos a cada requisição; a mudança fica na trilha com os escopos antes e depois.
 */
export function PermissionsTab({ integration }: { integration: Integration }) {
  const catalog = useIntegrationsCatalog()
  const update = useUpdateIntegration(integration.id)
  const formRef = useRef<HTMLFormElement>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const form = useFormState<IntegrationFormState>(
    () => ({
      name: integration.name,
      description: integration.description ?? '',
      scopes: integration.scopes,
    }),
    validateIntegration,
  )
  const readOnly = integration.status !== 'active'

  function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.check(INTEGRATION_KEYS)) {
      focusFirstInvalid(formRef.current)
      return
    }
    update.mutate(integrationInput(form.values), {
      onSuccess: () => {
        form.markSaved()
        setSavedAt(new Date())
        toast('Permissões salvas; valem na próxima chamada do app')
      },
      onError: (error) => {
        form.refuse(placeFieldErrors<IntegrationFormState>(error, integrationFieldKey).fields)
        focusFirstInvalid(formRef.current)
      },
    })
  }

  const failure = update.isError
    ? placeFieldErrors<IntegrationFormState>(update.error, integrationFieldKey)
    : null
  const showFailure =
    failure !== null && (Object.keys(failure.fields).length === 0 || failure.unplaced.length > 0)

  return (
    <form ref={formRef} onSubmit={submit} noValidate className="flex flex-col gap-4">
      {showFailure ? (
        <ActionError
          title="Não foi possível salvar"
          description={
            failure.unplaced.length > 0 ? failure.unplaced.join(' ') : errorMessage(update.error)
          }
        />
      ) : null}

      <FormBlock title="Identificação" note="O nome aparece na trilha em tudo que o app fizer.">
        <FieldGrid>
          <TextField
            form={form}
            name="name"
            label="Nome do app"
            maxLength={60}
            readOnly={readOnly}
          />
          <Field
            label="Descrição"
            optional={!readOnly}
            error={form.errors.description}
            meta={readOnly ? undefined : `${form.values.description.trim().length}/200`}
            className="col-span-full"
          >
            <Textarea
              rows={2}
              maxLength={200}
              readOnly={readOnly}
              value={form.values.description}
              onChange={(event) => form.update({ description: event.target.value })}
              onBlur={() => form.touch('description')}
            />
          </Field>
        </FieldGrid>
      </FormBlock>

      <FormBlock
        title="Permissões"
        note="O app faz, em cada recurso, o que a recepção faz, e nada além do que estiver marcado aqui. A mudança vale na próxima chamada."
      >
        {catalog.isError ? (
          <LoadError
            title="Não conseguimos carregar as permissões"
            error={catalog.error}
            onRetry={() => void catalog.refetch()}
          />
        ) : catalog.isPending ? (
          <div role="status" aria-label="Carregando as permissões" className="flex flex-col gap-3">
            <Skeleton className="h-[48px] w-full" />
            <Skeleton className="h-[48px] w-full" />
          </div>
        ) : (
          <PermissionsFields
            catalog={catalog.data}
            value={form.values.scopes}
            onChange={(scopes) => form.update({ scopes })}
            error={form.errors.scopes}
            disabled={readOnly}
          />
        )}
      </FormBlock>

      {readOnly ? null : (
        <FormFooter
          status={<SavedIndicator savedAt={savedAt} dirty={form.dirty} saving={update.isPending} />}
          primary={
            <Button type="submit" loading={update.isPending} loadingLabel="Salvando…">
              Salvar
            </Button>
          }
        />
      )}
    </form>
  )
}
